export const EN_TEMPLATES = {
    // Common punctuation and conjunctions
    common: {
        comma: ". ",
        period: ". ",
        bullet: "• ",
        newline: "\n",
        brackets: {
            left: "(",
            right: ")"
        },
        statistics: {
            mean: "mean",
            median: "median",
            mode: "mode",
            stdDev: "std dev",
            range: "range"
        },
        conjunction: {
            and: "and",
            with: "with",
            forms: "creating"
        }
    },

    // Area prefix templates
    prefixes: {
        global: "Overall",
        area: " area"
    },

    // Basic mappings
    mappings: {
        brightness: {
            BRIGHT: "bright",
            DARK: "dark",
            NEUTRAL: "neutral"
        },
        range: {
            COMPLETE: "complete",
            PARTIAL: "moderate",
            LIMITED: "limited"
        },
        tone: {
            STRONG_GREEN: "strong green",
            SLIGHT_GREEN: "slight green",
            GREEN: "green",
            NEUTRAL: "neutral",
            RED: "red",
            SLIGHT_RED: "slight red",
            STRONG_RED: "strong red"
        },
        temperature: {
            STRONG_BLUE: "strong blue",
            SLIGHT_BLUE: "slight blue",
            BLUE: "blue",
            NEUTRAL: "neutral",
            YELLOW: "yellow",
            SLIGHT_YELLOW: "slight yellow",
            STRONG_YELLOW: "strong yellow"
        },
        intensity: {
            STRONG: "strong",
            MODERATE: "moderate",
            WEAK: "weak"
        },
        distribution: {
            CONCENTRATED: "concentrated",
            SCATTERED: "scattered",
            VERY_SCATTERED: "very scattered"
        },
        skewness: {
            DARK_BIASED: "skewed towards shadows",
            LIGHT_BIASED: "skewed towards highlights",
            BALANCED: "balanced"
        },
        kurtosis: {
            CONCENTRATED: "concentrated",
            GENTLE: "gentle",
            FLAT: "flat"
        },
        colors: {
            red: "red areas",
            yellow: "yellow areas",
            green: "green areas",
            blue: "blue areas"
        },
        channels: {
            a: "a",
            b: "b",
            L: "L"
        },
        partitions: {
            blacks: "blacks ",
            shadows: "shadows ",
            midtones: "midtones ",
            highlights: "highlights ",
            whites: "whites "
        }
    },

    // Brightness templates
    brightness: {
        rules: {
            // Basic brightness rule templates
            basicBrightness: {
                darkComplete: "The image exhibits dark tonality with rich detail and complete dynamic range",
                darkLimited: "The image shows dark tonality with concentrated dynamic range",
                darkPartial: "The image presents dark tonality with narrow dynamic range",
                brightComplete: "The image displays bright tonality with rich detail and complete dynamic range",
                brightLimited: "The image shows bright tonality with concentrated dynamic range",
                brightPartial: "The image presents bright tonality with narrow dynamic range",
                neutralComplete: "The image maintains balanced brightness with complete dynamic range",
                neutralLimited: "The image shows balanced brightness with concentrated dynamic range",
                neutralPartial: "The image presents balanced brightness with narrow dynamic range"
            },
            
            // Feature rule templates
            featureDescriptions: {
                highContrast: "Strong contrast between highlights and shadows",
                lowKey: "Dominant low-key atmosphere with emphasis on shadows",
                highKey: "Prominent high-key atmosphere with emphasis on highlights"
            },
            // Detail description templates
            detailDescriptions: {
                richShadowDetail: "Rich detail preserved in shadow areas",
                wellControlledHighlight: "Well-preserved highlight detail"
            },

            // Rule matching warnings
            warnings: {
                noRuleMatched: "No matching rules found",
                templateMissing: "Template missing: ${rule}"
            }
        },
        
        description: {
            template: "${bullet}The overall image brightness is ${brightnessLevel}, with ${rangeLevel} dynamic range, distribution is ${skewnessClass} and ${kurtosisClass} (${channelL}, ${mean} ${meanValue}, ${median} ${medianValue}, ${range} ${rangeValue}${bracketRight}${period}"
        },
        // 添加亮度分析模板
        analysis: {
            noData: "Brightness characteristics are not available",
            noFeature: "No significant brightness characteristics detected",
            noBasicFeature: "No basic brightness features detected",
            conclusion: {
                basicFeature: "${feature}",
                withFeatureRule: "${basic}. ${feature}",
                withDetailRule: "${previous}. ${detail}",
                separator: ", "
            }
        }
    },
    // Tone Templates
    tones: {
        rules: {
            // Tone Analysis Templates
            mainTone: {
                toneSTRONG_GREEN: "Exhibits a strong green cast",
                toneSLIGHT_GREEN: "Shows a slight green cast",
                toneGREEN: "Has a green cast",
                toneNEUTRAL: "Maintains neutral tonality",
                toneRED: "Has a red cast",
                toneSLIGHT_RED: "Shows a slight red cast",
                toneSTRONG_RED: "Exhibits a strong red cast"
            },
            toneRange: {
                toneRangeConcentrated: "The tonal range is tightly concentrated",
                toneRangeScattered: "The tonal distribution shows good separation, enhancing depth",
                toneRangeVeryScattered: "The tonal distribution is broad, creating rich complexity"
            },
            toneStability: {
                toneStable: "The tone is stable, with a consistent overall feel",
                localWarmHighlight: "Localized warm highlights elevate the overall tone, enhancing warmth",
                localCoolShadow: "Localized cool shadows shift the overall tone, introducing a cool contrast",
                localToneDiversity: "Beyond the dominant tone, the contrast between red and green adds diversity and depth"
            },
            toneIntensity: {
                intensityStrong: "The tonal inclination is strong",
                intensityModerate: "The tonal inclination is moderate",
                intensityWeak: "The tonal inclination is weak"
            },
            toneDeviation: {
                deviationWeak: "The tone is close to neutral",
                deviationModerate: "The tone is balanced",
                deviationStrong: "The tone is distinct and clear"
            },
        },
        overallAnalysis: {
            consistency: {
                OVERALL_CONSISTENCY: "The tonal inclination is consistent across areas"
            },
            variations: {
                OVERALL_VARIATIONS: "Localized tonal variations add depth to the image"
            },
            contrast: {
                SHADOW_HIGHLIGHT_CONTRAST: "The contrast between shadows and highlights creates a striking tonal relationship"
            }
        },
        
        // 添加色调分析模板
        analysis: {
            noData: "No significant tonal characteristics detected",
            noRules: "No significant tonal characteristics detected",
            conclusion: {
                separator: ", ",
                period: ". ",
                partitionVariation: "with ${descriptions}",
                partitionTone: {
                    strong: "pronounced",
                    moderate: "slight",
                    red: "red bias",
                    green: "green bias",
                    neutral: "neutral tendency"
                }
            }
        }
    },
    temperature: {
        distribution: {
            balanced: "The temperature distribution is balanced",
            contrastive: "The contrast between warm and cool tones is evident",
            gradual: "The temperature transitions are smooth",
            warmDominant: "The image is dominated by warm tones",
            coolDominant: "The image is dominated by cool tones",
            neutralBalanced: "The balance between warm and cool tones is harmonious"
        },
        zoneContrast: {
            shadowsWarm: "Shadows have a warm tone",
            highlightsCool: "Highlights have a cool tone",
            midtonesNeutral: "Midtones are neutral",
            shadowsCool: "Shadows have a cool tone",
            highlightsWarm: "Highlights have a warm tone",
            gradualTransition: "The temperature transitions are natural",
            strongContrast: "The contrast between warm and cool tones is strong"
        },
        
        rules: {
            tone: {
                toneSTRONG_BLUE: "The color temperature exhibits a strong cool cast",
                toneSLIGHT_BLUE: "The color temperature shows a slight cool cast",
                toneBLUE: "The color temperature has a cool cast",
                toneNEUTRAL: "The color temperature remains neutral",
                toneYELLOW: "The color temperature has a warm cast",
                toneSLIGHT_YELLOW: "The color temperature shows a slight warm cast",
                toneSTRONG_YELLOW: "The color temperature exhibits a strong warm cast"
            },
            intensity: {
                intensityStrong: "The inclination is strong",
                intensityModerate: "The inclination is moderate",
                intensityWeak: "The inclination is weak"
            },
            stability: {
                stabilityStable: "The color temperature distribution is concentrated and stable",
                stabilityDominantCool: "Cool tones dominate with concentrated distribution of blue values",
                stabilityWarmHighlights: "Sparse warm highlights elevate the overall temperature, introducing subtle warmth",
                stabilityCoolWithWarmInfluence: "Cool tones predominate, with warm highlights creating localized warmth",
                stabilityWarmWithCoolContrast: "Warm tones prevail, with cool areas providing tonal contrast",
                stabilityBalancedContrast: "The color distribution shows balanced warm-cool contrast",
                stabilityUniform: "The color temperature distribution is uniform throughout"
            },
            deviation: {
                DEVIATION_WEAK: "The overall impression is close to neutral",
                DEVIATION_MODERATE: "The image has rich tonal layers",
                DEVIATION_STRONG: "The overall tone is distinctive"
            }
        },
        overallAnalysis: {
            consistency: {
                OVERALL_TEMP_CONSISTENCY: "Cold and warm tones are uniformly distributed across regions"
            },
            variations: {
                OVERALL_TEMP_VARIATIONS: "Localized variations in cold and warm tones add depth to the image"
            },
            contrast: {
                SHADOW_HIGHLIGHT_TEMP_CONTRAST: "Temperature contrasts between shadows and highlights enrich the tonal relationship"
            }
        },
        
        // 添加色温分析模板
        analysis: {
            noData: "No significant color temperature characteristics detected",
            noRules: "No significant color temperature characteristics detected",
            prefix: "",
            conclusion: {
                separator: ", ",
                period: ". ",
                partitionVariation: "with ${descriptions}",
                partitionTemp: {
                    strong: "pronounced",
                    moderate: "slight",
                    warm: "warm bias",
                    cool: "cool bias",
                    neutral: "neutral tendency"
                }
            }
        }
    },
    // 统计描述模板
    statistics: {
        L: "L channel",
        a: "a channel",
        b: "b channel",
        mean: "mean",
        median: "median",
        mode: "mode",
        stdDev: "std dev",
        range: "range"
    },
    // 不完整数据提示
    incomplete: {
        mainTone: "Main tone data incomplete",
        colorTemperature: "Color temperature data incomplete"
    },

    // Add local color description templates
    localColor: {
        description: {
            both: "Notable ${highlights} and significant ${lowlights} in local areas enhance tonal depth",
            highlights: "Notable ${highlights} in local areas create complementary contrast with the main tone",
            lowlights: "Significant ${lowlights} in local areas create complementary contrast with the main tone",
            balanced: "Even distribution of high and low value pixels creates balanced overall tonality",
            colorFormat: "${color} (${channel} channel max ${value})"
        }
    },
    // Add text format templates
    format: {
        doubleNewline: "\n\n",
        singleNewline: "\n",
        globalConclusion: {
            template: "${brightness}${doubleNewline}${mainTone}${singleNewline}${temperature}${doubleNewline}${localColor}"
        },
        partitionConclusion: {
            template: "${mainTone}${singleNewline}${temperature}${doubleNewline}${localColor}"
        },
    }
};